import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnInit, inject } from '@angular/core';
import { getMonthName } from '@celerofinancas/core-ts';
import { LoadUserAction } from '@celerofinancas/state-management';
import {
  BfmDashboardFinanceComponent,
  DASHBOARD_FINANCE_GRAPHS_DEFAULT_CONFIG,
  DASHBOARD_FINANCE_GRAPHS_TOKEN,
  FINANCIAL_STATISTIC,
  FORMAT_DATE_TICK,
} from '@celerofinancas/ui-reports';
import { buildInfoNotices } from '../../configs/info-notices';
import { BankHeaderComponent } from '@shared/bank-header/bank-header.component';
import { InfoCardComponent } from '@shared/info-card/info-card.component';
import { FailureWrapperComponent } from '@shared/failure-wrapper/failure-wrapper.component';
import { TokenExchange } from '@shared/models/token-exchange';

/**
 * X-axis label for "Resultado mensal" — `2026-08-01` becomes `Ago/26`.
 *
 * The chart uses the response key as a raw label when this token is not provided,
 * and the key is a full date because ngx-charts requires it (see
 * `PredictedVsDoneResponse` in the contracts package). Without the formatter the
 * axis would show `2026-08-01` instead of the month name. Same implementation as
 * `caixa-cl-webcomponents` (`src/app/configs/tokens.ts`).
 *
 * @param date response key, a full date in `YYYY-MM-DD`
 * @returns the short month label, such as `Ago/26`
 */
export function formatMonthTick(date: string): string {
  const [year, month] = date.split('-');
  return `${getMonthName(Number(month), true)}/${year.slice(2)}`;
}

/**
 * Horizontal padding of the card that wraps each chart, in pixels.
 *
 * It is the package's own card (`section.cds-card`), and 24px is the value it
 * ships with.
 */
const CHART_CARD_HORIZONTAL_PADDING = 24;

/**
 * The chart whose card is watched — see `remeasureChartsOnCardResize`.
 *
 * It is the line chart because it is the one that measures too early. The event it
 * triggers is global, so the bar chart re-measures with it.
 */
const CHART_SELECTOR = 'bfm-dashboard-line-graph';

/**
 * Overflow, in pixels, that counts as the chart still fitting.
 *
 * `scrollWidth` and `clientWidth` are integers, so a drawing that fits can still read
 * one pixel over.
 */
const OVERFLOW_TOLERANCE = 1;

/**
 * Width for the line and bar charts, measured from the card that holds them.
 *
 * ## Why this is needed
 *
 * Without it the charts overflow their card: the line chart by 18px and the bar
 * chart by 9px, measured on screen. The reason is in the package's own
 * `getWidthFromWindow`, which derives the width from the window rather than from
 * the container:
 *
 * ```js
 * const dashboardRowWidth = MAIN_CONTAINER_WIDTH_PERCENTAGE * window.innerWidth;  // 0.8
 * ```
 *
 * That 0.8 is the share of the viewport the dashboard takes **in the original
 * portal**. Here the component is mounted by whoever embeds it and takes whatever
 * width it is given, so the assumption breaks and the computed width lands above
 * the real one. The chart then draws past the card, and the SVG has no `viewBox`
 * — so clamping it with CSS would clip the axis instead of scaling it.
 *
 * The package leaves this exact hook for it (`customWidth`, with a `TODO` next to
 * the window-based branch), and it receives the real measurement: the
 * `clientWidth` of the chart's parent, which is the card. Discounting the card's
 * horizontal padding gives the space actually available inside it.
 *
 * @param parentWidth `clientWidth` of the card holding the chart
 * @returns the width available inside the card
 */
export function chartWidthFromCard(parentWidth: number): number {
  return parentWidth - 2 * CHART_CARD_HORIZONTAL_PADDING;
}

/**
 * `statisticProperties` inherited from the package default (titles already in
 * Portuguese), only turning `showLinkButton` off on the three blocks that enable
 * it by default. The "see more" link calls `onCardRedirect`, which we do not
 * configure — without that destination screen the link would be visible and lead
 * nowhere, a dead end in front of the client.
 */
/**
 * Demo dashboard screen. Renders `bfm-dashboard-finance`
 * (`@celerofinancas/ui-reports`), which self-gates: it only fires the service
 * request once `CompanyState` emits a truthy `pk`.
 *
 * That `pk` is only hydrated by `CoreRegistryInterceptor` (task 6), and the
 * interceptor only acts **in reaction to an HTTP request** — it intercepts, sees
 * `UserState.pk` empty, and ONLY THEN fires the whole chain by itself
 * (`LoadUserAction` → `LoadEconomicGroupsAction` → `LoadCompaniesAction` →
 * `SetCurrentCompanyAction` plus accounts, keychain and cost center) before
 * letting the original request through. Without a first request nothing triggers
 * the interceptor — and the natural candidate is `bfm-dashboard-finance` itself,
 * which is gated on exactly that `pk`: a circular deadlock (nobody asks, nobody
 * hydrates, nobody renders). That is why `ngOnInit` dispatches `LoadUserAction()`
 * — it is not an artificial call to "wake" the interceptor, it is the same action
 * any post-login screen has to dispatch to learn who the user is; the side effect
 * of triggering the bootstrap through the interceptor is the mechanism task 6
 * built on purpose. We do not chain `LoadEconomicGroupsAction` manually (unlike
 * `celerov2-ui-client`, which has no such interceptor and therefore chains it by
 * hand in `dashboard.component.ts:152`) because that would duplicate the chain the
 * interceptor already runs on its own.
 *
 * Mirrors `serasa-cl-webcomponents/src/app/pages/dashboard/dashboard.component.ts`
 * structurally, minus what is Serasa-specific (DDA acceptance, tracking, their
 * feature flags).
 *
 * `statistics` covers the three value cards plus the two charts of the Serasa
 * pattern. The cards come from `GET /v2/reports/dashboard/finance/`; the charts
 * (`lineDailyBalance` and `barMonthlyComparative`) come from
 * `GET /v2/reports/predicted_vs_done/get/`, which the mock now serves in the two
 * modes the package asks for — daily (two windows, backwards and forwards) and
 * monthly (`monthly_indicator=true`). `BfmDashboardFinanceComponent` fires all
 * three calls by itself from this list, through `loadConfiguredReports()`; there is
 * nothing to chain here.
 *
 * Which statistics are on the list, and why each one is in or out, is documented on
 * `statistics` itself. It used to be restated here too, and the two drifted: this copy
 * still described `lateExpense` as being used and `groupedPredicted` as left out, and
 * by then the opposite was true of both. Nothing about the list is repeated here any
 * more, so there is only one place to keep right.
 *
 * Nothing about late records reaches the screen, which is rule 3 of the card
 * ([86e33z2g3](https://app.clickup.com/t/86e33z2g3)): GetNet has no such information to
 * show. A test asserts the three late variants stay off the list.
 */
@Component({
  selector: 'dashboard',
  standalone: true,
  imports: [BfmDashboardFinanceComponent, BankHeaderComponent, FailureWrapperComponent, InfoCardComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss', './dashboard.overrides.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: FORMAT_DATE_TICK, useValue: formatMonthTick },
    {
      provide: DASHBOARD_FINANCE_GRAPHS_TOKEN,
      useValue: { ...DASHBOARD_FINANCE_GRAPHS_DEFAULT_CONFIG, customWidth: chartWidthFromCard },
    },
  ],
})
export class DashboardComponent extends TokenExchange implements OnInit {
  /**
   * Host element, watched until the chart mounts — see `remeasureChartsOnCardResize`.
   */
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * Destroy reference, which disconnects the mount observer with the component.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Notices for the info card, built for today because one of them names the month that
   * closed. See `configs/info-notices.ts` for which ones apply and why.
   *
   * Read once, at construction: the screen is not open across a month boundary in any
   * scenario worth the extra machinery of recomputing it.
   */
  public readonly infoNotices = buildInfoNotices(new Date());

  /**
   * Statistics proven end to end against the mock, in the order the reordering design
   * ([86e33z2f1](https://app.clickup.com/t/86e33z2f1)) puts them on screen: the two
   * realised value cards, the daily balance chart and the monthly result chart.
   *
   * ## The list shrank to four
   *
   * It used to carry nine. The reordering design shows five blocks, four of them from the
   * package plus the info card, and the decision was to follow it: the predicted value
   * cards, the balance forecast and the two donuts come off the screen.
   *
   * Worth naming the cost, because it is not obvious from here: the donuts are items 2 and
   * 6 of this same card set, both asked for by the designer, both fixed and both already
   * live. Removing them retires that work. The call was taken deliberately, with the
   * conflict on the table.
   *
   * ## Nothing about late records on this screen
   *
   * Rule 3 of card [86e33z2g3](https://app.clickup.com/t/86e33z2g3) requires hiding the
   * late-record cards: *"at GetNet it will not be possible to have this information"*. As
   * an acquirer, GetNet sees sale settlement, not an account with a due date — with no due
   * date there is no lateness. So the `late` bucket the endpoint returns is simply not
   * consumed here, and the mock keeps returning it because the real API does.
   *
   * Left out, with reasons: everything the design does not show, plus `barDailyBalance`
   * (the package ships no default for it and it repeats `lineDailyBalance` in bars) and
   * `sessionCustom` (Serasa's DDA activation invitation, which does not exist in this
   * demo).
   */
  public readonly statistics = [
    FINANCIAL_STATISTIC.doneRevenue,
    FINANCIAL_STATISTIC.doneExpense,
    FINANCIAL_STATISTIC.lineDailyBalance,
    FINANCIAL_STATISTIC.barMonthlyComparative,
  ];

  /**
   * Does not exclude transitory accounts — the same data surface that was validated.
   */
  public readonly noTransitoryAccounts = false;

  /**
   * Fires the core registry bootstrap — see the class comment for the full
   * reasoning. Without this dispatch `bfm-dashboard-finance` never receives a
   * `company_pk` and the three blocks stay stuck at R$ 0,00.
   */
  public ngOnInit(): void {
    this.store.dispatch(new LoadUserAction());
    this.remeasureChartsOnCardResize();
  }

  /**
   * Asks the chart to measure itself again whenever what it drew stops matching the
   * space it has.
   *
   * `chartWidthFromCard` above gives the package the right formula, and the bar chart
   * lands exactly inside its card with it. The line chart does not, and the reason is
   * when it measures rather than how. Sampling the card every 50ms from the first paint
   * shows it:
   *
   * ```
   * 213ms  card=683  svg=635
   * 254ms  card=643  svg=635
   * ```
   *
   * The card is born wider than it ends up, the chart measures at 213ms and keeps that
   * number, and at 254ms the card takes its final width without the chart following.
   *
   * The package watches the parent with a `ResizeObserver` for exactly this and does not
   * recover it. Its other listener does: `window:resize` recomputes from the settled
   * layout. So this watches the card and fires that event.
   *
   * ## Why the condition is a comparison and not "who resized"
   *
   * The first version asked a different question: it skipped the event whenever the
   * window was the thing being resized, on the grounds that the package had already been
   * told. That shipped, and the chart still overflowed by 8px in the published
   * environment.
   *
   * What it missed is that the page growing a scrollbar changes the viewport, which the
   * browser reports as a window resize. The package did recompute for it, from the
   * layout as it stood at that instant, before the card had reflowed to its new width —
   * so the answer was stale and the guard stayed quiet about it. It traded the original
   * bug for a narrower version of the same one.
   *
   * Asking whether the drawing still fits the box is the question that actually matters.
   * When the package got it right there is nothing to send, which is what keeps the host
   * page from handling every resize twice; when the drawing spills out the event goes,
   * whatever caused it. Measured on the published environment, in both states:
   *
   * ```
   * stale       scrollWidth=602  clientWidth=595   <- spills, so it asks
   * corrected   scrollWidth=595  clientWidth=595   <- fits, so it stays quiet
   * ```
   *
   * The bar chart reads 595/595 in both, and is never asked about.
   *
   * ## Why not compare the width the chart wrote on its `svg`
   *
   * That was the first attempt at this condition and it is order-dependent: the chart
   * renders two `svg` elements, one sized and one holding gradient definitions, and
   * `querySelector` returns whichever comes first. It comes out sized in the browser and
   * unsized under the test runner, so the condition read `null` and never fired.
   *
   * The trade-off taken here is that a chart drawn *narrower* than its card is left
   * alone, since it leaves whitespace rather than spilling, and no item reports it.
   *
   * The event is global, and in production this component lives inside the client's
   * portal, which will receive a `resize` it did not cause. The package exposes no other
   * way to ask for a re-measure.
   */
  private remeasureChartsOnCardResize(): void {
    const host = this.elementRef.nativeElement;

    const watchCard = (chart: Element): void => {
      const card = chart.parentElement;

      if (!card) {
        return;
      }

      // One request per card width. The package answers by recomputing from its own
      // formula, and if that ever lands on a different number than ours the comparison
      // below would keep disagreeing — this is what stops it from asking forever.
      let askedFor = -1;

      const cardObserver = new ResizeObserver(() => {
        const overflow = chart.scrollWidth - chart.clientWidth;

        if (overflow <= OVERFLOW_TOLERANCE || askedFor === card.clientWidth) {
          return;
        }

        askedFor = card.clientWidth;
        window.dispatchEvent(new Event('resize'));
      });

      cardObserver.observe(card);
      this.destroyRef.onDestroy(() => cardObserver.disconnect());
    };

    const mounted = host.querySelector(CHART_SELECTOR);

    if (mounted) {
      watchCard(mounted);

      return;
    }

    const mountObserver = new MutationObserver(() => {
      const chart = host.querySelector(CHART_SELECTOR);

      if (!chart) {
        return;
      }

      mountObserver.disconnect();
      watchCard(chart);
    });

    mountObserver.observe(host, { childList: true, subtree: true });
    this.destroyRef.onDestroy(() => mountObserver.disconnect());
  }
}
